import type { ComponentProps, ReactNode } from 'react'
import { Spinner } from '@/shared/components/spinner'
import { cn } from '@/shared/lib/utils'

const VARIANT_COLORS = {
  primary: 'var(--color-app-primary-600)',
  secondary: 'transparent',
  contrast: 'var(--color-app-primary-900)',
  light: '#ffffff',
  error: 'var(--theme-error-button)',
} as const

interface ButtonProps extends ComponentProps<'button'> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'contrast' | 'light' | 'error'
  loading?: boolean
}

export function Button({
  children,
  variant = 'primary',
  loading = false,
  className,
  style,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || loading}
      style={{
        backgroundColor: VARIANT_COLORS[variant],
        height: 'clamp(56px, 3.5rem, 64px)',
        transitionProperty: 'all',
        transitionDuration: '300ms',
        ...style,
      }}
      className={cn(
        'flex w-full cursor-pointer items-center justify-center gap-[8px] rounded-full text-lg font-semibold tracking-[-0.36px] transition-[filter] duration-200 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:brightness-100',
        variant === 'secondary'
          ? 'border-2 border-app-primary-600 text-app-primary-600'
          : variant === 'light'
            ? 'text-app-primary-700'
            : 'text-white',
        className,
      )}
    >
      {children}
      {/* At rest it takes back the gap before it, so the label sits centred. */}
      <Spinner
        className={cn(
          'shrink-0 transition-all duration-300',
          loading ? 'size-5 opacity-100' : '-ml-[8px] size-0 opacity-0',
          variant === 'secondary' || variant === 'light'
            ? 'text-app-primary-600'
            : 'text-white',
        )}
      />
    </button>
  )
}
